import { MutableRefObject, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import useDirectory from 'hooks/useDirectory'
import useMutation from 'hooks/useMutation'
import { useRecoilState, useRecoilValue } from 'recoil'
import {
  buildingModeState,
  listPointState,
  selectedPoint,
  selectedPointDetail
} from 'store/buildingStore'
import useSWR from 'swr'
import Utils from 'utils'
import Krpano from 'utils/krpano'

import { rotatedMarkerIcon, rotatedMarkerIconDense } from '../CustomMarker'

type Props = {
  leafletRef: MutableRefObject<any>
}

export default function useRadar({ leafletRef }: Props) {
  const { id: buildingId, floorId } = useParams()
  const { navigate } = useDirectory()

  const buildingMode = useRecoilValue(buildingModeState)
  const currentSelectedPoint = useRecoilValue(selectedPoint)
  const [pointDetail, setPointDetail] = useRecoilState(selectedPointDetail)
  const [, setPoints] = useRecoilState(listPointState)

  const { trigger: handlePoint, isMutating } = useMutation('v1/points')
  const { mutate: refetchPoint } = useSWR(
    {
      url: 'v1/points',
      params: {
        buildingId,
        floorId
      }
    },
    {
      revalidateOnMount: false
    }
  )

  const radarEventHandler = (mode: string) => {
    const isEditable = pointDetail && mode === 'point'

    return {
      click() {
        if (isEditable) {
          const handleClick = () => {
            if (!leafletRef.current.radarDragFlag.isRadarMode) {
              leafletRef.current.radarDragFlag.isRadarMode = true
              leafletRef.current.mapContainerRef.dragging.disable()
              leafletRef.current.povMarkerRef.setIcon(
                rotatedMarkerIconDense(leafletRef.current.radarDragFlag.rotate)
              )
              return
            }
            leafletRef.current.radarDragFlag.isRadarMode = false
            leafletRef.current.mapContainerRef.dragging.enable()
            leafletRef.current.povMarkerRef.setIcon(
              rotatedMarkerIcon(leafletRef.current.radarDragFlag.rotate)
            )
            // clear timeout to stop mousedown event
            leafletRef.current.radarDragFlag.mouseHoldTime &&
              clearTimeout(leafletRef.current.radarDragFlag.mouseHoldTime)
            // update new h gap between image360 and radar

            const { v, z, h } = pointDetail.initView

            !isMutating &&
              handlePoint(
                {
                  method: 'patch',
                  query: [currentSelectedPoint],
                  data: {
                    floor_id: +(floorId || 0),
                    building_id: +(buildingId || 0),
                    init_view: {
                      h,
                      v,
                      z,
                      radar_h_gap: Krpano.NormalizeHGap(
                        leafletRef.current.radarDragFlag.hgap
                      )
                    }
                  }
                },
                {
                  onSuccess: async () => {
                    await refetchPoint().then((data) => {
                      setPoints(data?.points)

                      const currentPoint = data?.points?.find(
                        (p: any) => p?.id === pointDetail?.id
                      )
                      setPointDetail(currentPoint)

                      Krpano.RevalidateDirection(currentPoint?.directions || [])
                    })
                  },
                  onError: (error) => {
                    Utils.handleErrorNavigate(error, navigate)
                  }
                }
              )
          }
          Krpano.LoadChecking(handleClick)
        }
      },
      mousedown() {
        if (leafletRef.current.radarDragFlag.isRadarMode) {
          leafletRef.current.radarDragFlag.mouseHoldTime = setTimeout(() => {
            leafletRef.current.radarDragFlag.isDragging = true
          }, 200)
        }
      },
      mouseup() {
        if (
          buildingMode.selectedMode === 'link' &&
          leafletRef.current.createPolylineRef
        ) {
          leafletRef.current.mapContainerRef?.removeLayer(
            leafletRef.current.createPolylineRef
          )
          leafletRef.current.createPolylineRef = null
          leafletRef.current.mapContainerRef?.dragging.enable()
        }
      }
    }
  }

  const sync360ViewToArc = (icon: any) => {
    ;(window as any).sync_360viewToArc = (hlookat: any) => {
      const h = Krpano.NormalizeHGap(hlookat)
      if (
        !leafletRef.current.radarDragFlag.isRadarMode &&
        leafletRef.current.radarDragFlag.hgap !== null
      ) {
        leafletRef.current.radarDragFlag.rotate = Krpano.NormalizeHGap(
          h + leafletRef.current.radarDragFlag.hgap
        )
        leafletRef.current.povMarkerRef?.setIcon(
          icon(leafletRef.current.radarDragFlag.rotate)
        )
      }
      if (leafletRef.current.radarDragFlag.isRadarMode) {
        leafletRef.current.radarDragFlag.hgap = Krpano.NormalizeHGap(
          leafletRef.current.radarDragFlag.rotate - h
        )
      }
    }
  }

  useEffect(() => {
    //  find position & add pov marker into map
    const handleDisplayPov = () => {
      if (!currentSelectedPoint || currentSelectedPoint !== pointDetail?.id) {
        leafletRef.current.radarDragFlag = {
          isRadarMode: false,
          isDragging: false,
          rotate: null,
          hgap: null,
          mouseHoldTime: null
        }
        leafletRef.current.mapContainerRef?.dragging.enable()
        return
      }
      if (pointDetail) {
        const hlookat = (
          document.getElementById('embedpano-preview') as any
        )?.get('view.hlookat')
        const rotate =
          hlookat !== pointDetail.initView.h
            ? Krpano.NormalizeHGap(hlookat)
            : pointDetail.initView.h
        leafletRef.current.radarDragFlag = {
          isRadarMode: false,
          isDragging: false,
          rotate: Krpano.NormalizeHGap(
            leafletRef.current.radarDragFlag.rotate ||
              rotate + pointDetail.initView.radarHGap
          ),
          hgap: pointDetail.initView.radarHGap,
          mouseHoldTime: null
        }
        if (leafletRef.current.povMarkerRef) {
          leafletRef.current.povMarkerRef.setIcon(
            rotatedMarkerIcon(
              Krpano.NormalizeHGap(leafletRef.current.radarDragFlag.rotate)
            )
          )
        }
      }
    }
    handleDisplayPov()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSelectedPoint, pointDetail])

  useEffect(() => {
    if (
      pointDetail &&
      leafletRef.current.radarDragFlag.isRadarMode &&
      !buildingMode.selectedMode
    ) {
      const hlookat = Krpano.NormalizeHGap(
        (document.getElementById('embedpano-preview') as any)?.get(
          'view.hlookat'
        )
      )
      leafletRef.current.radarDragFlag.isRadarMode = false
      leafletRef.current.radarDragFlag.hgap = pointDetail.initView.radarHGap
      leafletRef.current.radarDragFlag.rotate =
        hlookat + pointDetail.initView.radarHGap
      leafletRef.current.mapContainerRef?.dragging.enable()
      if (leafletRef.current.povMarkerRef) {
        leafletRef.current.povMarkerRef.setIcon(
          rotatedMarkerIcon(hlookat + pointDetail.initView.radarHGap)
        )
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildingMode.selectedMode, pointDetail])

  return {
    sync360ViewToArc,
    radarEventHandler
  }
}
